import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { MarketService } from "../../../../../../shared/src/lib/services/market.service";
import { AsyncPipe, DatePipe, NgClass, NgStyle } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { NavService } from "../../../../../../shared/src/lib/services/nav.service";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { combineLatest, map, Observable, Subject } from "rxjs";
import { NavItemLoaderComponent } from "./nav-item-loader/nav-item-loader.component";
import { MarketOpsService } from "../../content/market-layout/market/market-ops.service";
import { FavoritesService } from "../../../../../../shared/src/lib/services/favorites.service";
import { layLessSports } from "../../../../assets/constants/market";
import { takeUntil } from "rxjs/operators";
import { sortLineMarketNavItems } from "../../../../../../shared/src/lib/utils/sort-line-markets";

function commonParents(navItems: any[] = []) {
  if (!navItems.length) return [];

  const commonIds = navItems
    .map((item) => new Set((item?.parents ?? []).map((p: any) => p?.name)))
    .reduce((acc, set) => {
      for (const name of acc) if (!set.has(name)) acc.delete(name);
      return acc;
    });

  // Return full parent objects (from the first item) for the common ids
  return (navItems[0]?.parents ?? []).filter((p: any) => commonIds.has(p?.name));
}

@Component({
  selector: "app-navigation-item",
  imports: [NgClass, RouterLink, NgStyle, MatIconModule, DatePipe, NavItemLoaderComponent, AsyncPipe],
  templateUrl: "./navigation-item.component.html",
  styleUrl: "./navigation-item.component.scss",
})
export class NavigationItemComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  itemType = "";
  id = "";
  loader = inject(LoaderService);
  marketService = inject(MarketService);
  navService = inject(NavService);
  navItems: any[] = [];
  filteredNavItems: any[] = [];
  private searchQuery = "";
  parents: any[] = [];
  currentUrl: any;
  $loadingSports!: Observable<boolean>;
  $loadingChildren!: Observable<boolean>;
  marketOps = inject(MarketOpsService);
  favoritesService = inject(FavoritesService);
  route = inject(ActivatedRoute);
  router = inject(Router);

  get showStartTime() {
    return this.parents?.some((p) => p?.type === "EVENT_TYPE" && layLessSports.includes(p?.id));
  }

  ngOnInit() {
    this.$loadingSports = combineLatest([this.loader.isLoading("/navigation/sports"), this.loader.isLoading("/v1/api/navigation_list")]).pipe(
      map(([loadingSports, loadingNavigationList]) => loadingSports || loadingNavigationList),
    );

    this.$loadingChildren = combineLatest([
      this.loader.isLoading("/navigation/group"),
      this.loader.isLoading("/navigation/event"),
      this.loader.isLoading("/v1/api/navigation_list"),
    ]).pipe(map(([loadingGroups, loadingEvents, loadingNavigationList]) => loadingGroups || loadingEvents || loadingNavigationList));

    this.handleMarketAlert();
    this.currentUrl = this.router.url;

    this.router.events.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.currentUrl = this.router.url;
    });

    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.itemType = params["type"];
      this.id = params["id"];
      this.fetchNavItems(params["type"], params["id"]);
    });

    this.navService.sportsSearchQuery.pipe(takeUntil(this.destroy$)).subscribe((query) => {
      this.searchQuery = query;
      this.applySearch();
    });
  }

  handleMarketAlert() {
    this.marketService
      .getMarketAlert()
      .pipe(takeUntil(this.destroy$))
      .subscribe((marketId: string) => {
        if (marketId) {
          // console.log("Closed market alert", marketId);
          // console.log("Markets", this.navItems);
          this.marketService.setMarketAlert("");
          const marketIds = this.navItems?.map((market: any) => `bf_${market?.id}`);
          const params = { market_ids: marketIds?.join(",") };
          this.getMarketStatus(params, this.navItems);
        }
      });
  }

  fetchNavItems(type: any, id: any) {
    if (type === "event_type") {
      this.fetchSportTypes(id);
    } else if (type === "group") {
      this.fetchGroups(id);
    } else if (["event", "race"].includes(type)) {
      this.fetchEvents(id);
    }
  }

  setParents(data: any[]) {
    // console.log(data);
    const eventType = data.find((parent) => parent.type === "EVENT_TYPE");
    this.parents = data.map((item, index) => {
      if (item.type !== "EVENT_TYPE" && eventType?.id) {
        // console.log(index);
        let parentIds: Record<string, string> = {};

        if (index > 1) {
          for (let i = index - 1; i > 0; i--) {
            parentIds[`parent-${i}`] = data?.[i]?.id;
          }
          // console.log("Parent Ids for ", index, parentIds);
        }

        return {
          ...item,
          eventTypeId: eventType?.id,
          ...parentIds,
        };
      }

      return item;
    });
    // console.log(this.parents);
  }

  setNavItems(data: any[]) {
    this.navItems = data?.map((navItem) => {
      const parentIds = Object.fromEntries((navItem?.parents ?? []).map((parent: any, index: number) => [`parent-${index}`, parent.id]));
      return { ...navItem, ...parentIds };
    });

    const priorityMap = new Map(this.navItems.map((item: any, index) => [item.id, index]));
    this.marketOps.navBasedSortPriority$.next(priorityMap);
    this.applySearch();
  }

  private applySearch() {
    if (!this.searchQuery?.trim()) {
      this.filteredNavItems = this.navItems;
      return;
    }
    const q = this.searchQuery.toLowerCase();
    this.filteredNavItems = this.navItems.filter((item) => item.name?.toLowerCase().includes(q));
  }

  isImmediateParent(index: number) {
    return this.parents.length - 1 === index;
  }

  fetchSportTypes(id: string) {
    this.marketService
      .getSportType({ id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && Array.isArray(response?.data)) {
            this.setNavItems(response?.data);
            const fixtures = response?.data?.[0]?.aliases?.FIXTURES;
            this.setParents(fixtures?.length ? fixtures : commonParents(response?.data));
          }
        },
        error: (error: any) => {
          console.error(error);
        },
      });
  }

  fetchGroups(id: string) {
    this.marketService
      .getGroup({ id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && Array.isArray(response?.data)) {
            this.setNavItems(response?.data);
            this.setParents(commonParents(response?.data));
          }
        },
        error: (error: any) => {
          console.error(error);
        },
      });
  }

  fetchEvents(id: string) {
    this.marketService
      .getEvent({ id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response && Array.isArray(response?.data)) {
            this.setNavItems(sortLineMarketNavItems(response?.data));
            this.setParents(commonParents(response?.data));
          }
        },
        error: (error: any) => {
          console.error(error);
        },
      });
  }

  getMarketStatus(params: any, markets: any) {
    const openEventTypes = ["7", "4339"]; // event type ids (horse & greyhound racing) - only get open markets
    const eventType = markets?.[0]?.parents?.[0]?.id; // event type id (first parent) of the current set of markets
    const qParams = {
      ...params,
      statuses: openEventTypes?.includes(eventType) ? "2" : "2,3",
      return_snapshot: "N",
    };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          const { statuses = {} } = response || {};

          const openedMarkets = statuses["2"] || [];
          const suspendedMarkets = statuses["3"] || [];
          const _markets = markets?.filter((market: any) => {
            const _marketId = `bf_${market?.id}`;
            return openedMarkets.includes(_marketId) || suspendedMarkets.includes(_marketId);
          });

          // console.log("markets after status filter => ", _markets);

          if (_markets.length > 0) {
            this.navItems = _markets;
            this.applySearch();
            const firstMarket = _markets?.[0];
            const firstMarketParents = firstMarket?.parents;
            const firstMarketId = firstMarket?.id;
            const eventId = firstMarketParents[firstMarketParents?.length - 1]?.id;

            this.setParents(firstMarketParents);
            // this.navigateToFirstMarket(firstMarketId, eventId);
          } else {
            // console.log(this.parents);
            if (this.parents.length > 0) {
              const eventId = this.parents?.[this.parents?.length - 1]?.id;
              this.navigateToEvent(eventId);
            }
          }
        },
        error: (error) => {
          console.error("Failed to fetch market statuses:", error);
        },
      });
  }

  navigateToEvent(eventId: string) {
    this.router.navigate([
      "/app/main",
      {
        outlets: {
          navigation: ["event", eventId],
          content: ["event-markets", eventId],
        },
      },
    ]);
  }

  navigateToFirstMarket(marketId: string, eventId: string) {
    // console.log("navigate to event => ", eventId);
    // console.log("navigate to market => ", marketId);
    this.router.navigate([
      "/app/main",
      {
        outlets: {
          navigation: ["event", eventId],
          content: ["market", marketId],
        },
      },
    ]);
  }

  isViewableItem(item: any) {
    return ["EVENT", "MARKET", "RACE"].includes(item?.type);
  }

  handleClick(item: any) {
    if (item?.type === "MARKET") {
      this.marketOps.navMarket$.next(item?.id);
    }
    if (window.innerWidth < 1280 && this.isViewableItem(item)) {
      this.navService.openSportsBar.next(false);
    }
  }

  handleFavorites(item: any, e: Event) {
    e.stopPropagation();
    e.preventDefault();
    // console.log(item);
    if (this.isFavorite(item)) {
      this.favoritesService.remove(item, item.type);
    } else {
      this.favoritesService.add(item, item.type);
    }
  }

  isFavorite(item: any) {
    return this.favoritesService.isFavorite(item.id, item.type);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
